/** Centered OAuth popup helper (extracted from IntegrationsPage). */
export function openOAuthPopup(url: string, title: string): Window | null {
    const width = 600, height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    return window.open(url, title, `width=${width},height=${height},left=${left},top=${top}`);
}

export type OAuthPopupResult = "message" | "verified" | "closed" | "timeout";

interface OAuthPopupWaitOptions {
    authorizationUrl: string;
    messageTypes: readonly string[];
    completionCheck?: () => boolean | Promise<boolean>;
    completionCheckIntervalMs?: number;
    timeoutMs?: number;
}

const DEFAULT_TIMEOUT_MS = 10 * 60 * 1000;
const DEFAULT_COMPLETION_CHECK_INTERVAL_MS = 1000;

/**
 * Wait for an OAuth callback without continuously reading `popup.closed`.
 *
 * OAuth providers run on a different origin and may apply COOP headers that
 * make repeated access to the popup window noisy or unreliable. Successful
 * callback pages already notify their opener with postMessage, so that is the
 * primary completion signal. An optional authenticated completion check lets
 * callers observe the credential update when a provider severs the opener
 * relationship. A close check on focus/visibility preserves the existing
 * manual-close behaviour without polling the cross-origin window.
 *
 * The caller must still verify the credential with the authenticated backend
 * before treating any result as authorization success. This helper only says
 * that the popup interaction has finished.
 */
export function waitForOAuthPopup(
    popup: Window,
    {
        authorizationUrl,
        messageTypes,
        completionCheck,
        completionCheckIntervalMs = DEFAULT_COMPLETION_CHECK_INTERVAL_MS,
        timeoutMs = DEFAULT_TIMEOUT_MS
    }: OAuthPopupWaitOptions
): Promise<OAuthPopupResult> {
    const trustedOrigins = new Set<string>([window.location.origin]);
    try {
        const redirectUri = new URL(authorizationUrl, window.location.href)
            .searchParams.get("redirect_uri");
        if (redirectUri) {
            const redirectOrigin = new URL(redirectUri, window.location.href).origin;
            if (redirectOrigin.startsWith("http://") || redirectOrigin.startsWith("https://")) {
                trustedOrigins.add(redirectOrigin);
            }
        }
    } catch {
        // The authorization URL has already been produced by the backend. If
        // it cannot be parsed here, restrict messages to the current origin.
    }

    const acceptedTypes = new Set(messageTypes);

    return new Promise(resolve => {
        let settled = false;
        let closeCheckTimer: number | undefined;
        let completionCheckTimer: number | undefined;
        let timeoutTimer: number;

        const settle = (result: OAuthPopupResult) => {
            if (settled) return;
            settled = true;
            window.removeEventListener("message", onMessage);
            window.removeEventListener("focus", scheduleCloseCheck);
            document.removeEventListener("visibilitychange", onVisibilityChange);
            window.clearTimeout(timeoutTimer);
            if (closeCheckTimer !== undefined) window.clearTimeout(closeCheckTimer);
            if (completionCheckTimer !== undefined) window.clearTimeout(completionCheckTimer);
            resolve(result);
        };

        const onMessage = (event: MessageEvent) => {
            if (event.source !== popup || !trustedOrigins.has(event.origin)) return;
            const type = (event.data as { type?: unknown } | null)?.type;
            if (typeof type === "string" && acceptedTypes.has(type)) settle("message");
        };

        const checkClosed = () => {
            closeCheckTimer = undefined;
            try {
                if (popup.closed) settle("closed");
            } catch {
                // COOP can temporarily deny access while the provider page is
                // cross-origin. The callback message or timeout remains active.
            }
        };

        const scheduleCloseCheck = () => {
            if (closeCheckTimer !== undefined) window.clearTimeout(closeCheckTimer);
            closeCheckTimer = window.setTimeout(checkClosed, 0);
        };

        const onVisibilityChange = () => {
            if (document.visibilityState === "visible") scheduleCloseCheck();
        };

        const scheduleCompletionCheck = () => {
            if (!completionCheck || settled) return;
            completionCheckTimer = window.setTimeout(async () => {
                completionCheckTimer = undefined;
                try {
                    if (await completionCheck()) {
                        settle("verified");
                        return;
                    }
                } catch {
                    // A transient status failure must not end the OAuth flow.
                    // The next bounded check can still observe completion.
                }
                scheduleCompletionCheck();
            }, completionCheckIntervalMs);
        };

        window.addEventListener("message", onMessage);
        window.addEventListener("focus", scheduleCloseCheck);
        document.addEventListener("visibilitychange", onVisibilityChange);
        scheduleCompletionCheck();
        timeoutTimer = window.setTimeout(() => settle("timeout"), timeoutMs);
    });
}
